import {
  DELIVERY_DESTINATION_ID,
  DELIVERY_ORDER_TYPE,
  IN_STORE_DESTINATION_ID,
} from '../constants';
import { IGetCartPayload } from '../models/order.model';
import {
  PULSE_CLICK_ACTION,
  PULSE_CLICK_CATEGORY,
  resolveHeaderPulseClickCategory,
  trackPulseClick,
} from './pulseClickIds';
import { PULSE_VIEW_ID } from './pulseViewIds';

/** Set when cart icon is tapped on checkout (route may change before get-cart returns). */
let checkoutTapOriginPath: string | null = null;

const PULSE_TAP_CART_ICON_PENDING_KEY = 'pulse_tap_cart_icon_pending';

/** User tapped the cart icon — allow one tap_cart_icon after get-cart (not on persist rehydrate). */
export const armTapCartIconClick = (): void => {
  try {
    sessionStorage.setItem(PULSE_TAP_CART_ICON_PENDING_KEY, '1');
  } catch (_) {}
};

export const isTapCartIconClickPending = (): boolean => {
  try {
    return sessionStorage.getItem(PULSE_TAP_CART_ICON_PENDING_KEY) === '1';
  } catch (_) {
    return false;
  }
};

export const disarmTapCartIconClick = (): void => {
  try {
    sessionStorage.removeItem(PULSE_TAP_CART_ICON_PENDING_KEY);
  } catch (_) {}
};

export const setTapCartIconCheckoutOrigin = (page_path: string): void => {
  checkoutTapOriginPath = page_path;
};

export const resolveTapCartIconPagePath = (fallbackPath: string): string => {
  if (!checkoutTapOriginPath) return fallbackPath;
  const path = checkoutTapOriginPath;
  checkoutTapOriginPath = null;
  return path;
};

const isCheckoutPagePath = (pathname: string): boolean =>
  pathname.includes('/checkout');

export type TapCartIconCartSnapshot = {
  cartId?: string | number | null;
  itemCount?: number | null;
};

/** Map get-cart API body → tap_cart_icon snapshot (always produces a snapshot). */
export const snapshotFromCartApiResponse = (
  response: { id?: string | number | null; items?: unknown[] | null } | null | undefined
): TapCartIconCartSnapshot => ({
  cartId: response?.id ?? null,
  itemCount: Array.isArray(response?.items) ? response.items.length : 0,
});

/** Build get-cart payload for Pulse read (mirrors cart sync inputs, analytics-only). */
export const buildPulseGetCartPayload = ({
  locationId,
  customerId,
  orderType,
}: {
  locationId: string | number;
  customerId: string | number;
  orderType?: string | null;
}): IGetCartPayload => {
  const payload: IGetCartPayload = {
    location_id: String(locationId),
    customer_id: String(customerId),
  };
  if (orderType) {
    payload.order_type =
      orderType === DELIVERY_ORDER_TYPE
        ? DELIVERY_DESTINATION_ID
        : IN_STORE_DESTINATION_ID;
  }
  return payload;
};

/** Build tap_cart_icon click after cart API response (empty cart included). */
export const buildTapCartIconClickArgs = ({
  pathname,
  rewardsCategory = null,
  cart,
  page_path,
}: {
  pathname: string;
  rewardsCategory?: string | null;
  cart: TapCartIconCartSnapshot;
  page_path?: string;
}) => {
  const path = page_path ?? pathname;
  const itemCount = cart.itemCount ?? 0;

  if (isCheckoutPagePath(path)) {
    return {
      category: PULSE_CLICK_CATEGORY.CHECKOUT_PAGE,
      action: PULSE_CLICK_ACTION.TAP_CART_ICON,
      label: String(itemCount),
      value: itemCount,
      view_id: PULSE_VIEW_ID.CHECKOUT,
      page_id: PULSE_CLICK_CATEGORY.CHECKOUT_PAGE,
      page_path: path,
    };
  }

  const category = resolveHeaderPulseClickCategory(pathname, rewardsCategory);
  if (!category) return null;

  return {
    category,
    action: PULSE_CLICK_ACTION.TAP_CART_ICON,
    label: String(itemCount),
    value: itemCount,
    page_id: category,
    page_path: path,
  };
};

/**
 * Pulse `tap_cart_icon` — fire on get-cart response when cart opens.
 * Empty cart and missing items array are valid; always sends a snapshot.
 */
export const trackTapCartIcon = (params: {
  pathname: string;
  rewardsCategory?: string | null;
  cart: TapCartIconCartSnapshot;
  page_path?: string;
}): void => {
  const clickArgs = buildTapCartIconClickArgs(params);
  if (!clickArgs) return;
  trackPulseClick(clickArgs);
};
